import React, { Component } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import { Button, ButtonGroup, Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { DatePicker } from "antd";
import moment from "moment";

import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import {
    fetchIngestionFailures,
    clearIngestionFailures
} from "api/menu/hr/ingestion-failures/actions";

const { RangePicker } = DatePicker;

class IngestionFailuresList extends Component {
    constructor(props) {
        super(props);
        
        // Default to last 7 days
        const defaultFromDate = moment().subtract(7, 'days').startOf('day');
        const defaultToDate = moment().endOf('day');
        
        this.state = {
            fromUtc: defaultFromDate,
            toUtc: defaultToDate,
            dateRange: [defaultFromDate, defaultToDate]
        };
    }

    componentDidMount() {
        this.handleSearch();
    }

    componentWillUnmount() {
        this.props.clearIngestionFailures();
    }

    handleSearch = () => {
        const { fromUtc, toUtc } = this.state;
        const filters = {
            fromUtc: fromUtc ? fromUtc.toISOString() : null,
            toUtc: toUtc ? toUtc.toISOString() : null
        };
        this.props.fetchIngestionFailures(filters);
    };

    handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            this.setState({
                dateRange: dates,
                fromUtc: dates[0].startOf('day'),
                toUtc: dates[1].endOf('day')
            });
        } else {
            this.setState({
                dateRange: null,
                fromUtc: null,
                toUtc: null
            });
        }
    };

    render() {
        const { ingestionFailures, loading } = this.props;
        const { dateRange } = this.state;

        const columns = [
            { 
                Header: "ID", 
                accessor: "id", 
                id: "id", 
                width: 80 
            },
            { 
                Header: "Occurred", 
                id: "occurredUtc", 
                accessor: original => new Date(original.occurredUtc).toLocaleString(),
                width: 180
            },
            { 
                Header: "Mailbox", 
                accessor: "mailbox", 
                id: "mailbox",
                width: 200
            },
            { 
                Header: "Subject", 
                accessor: "subject", 
                id: "subject"
            },
            { 
                Header: "Has Attachments", 
                accessor: "hasAttachments", 
                id: "hasAttachments",
                Cell: ({ value }) => value ? "Yes" : "No",
                width: 130
            },
            { 
                Header: "Validation Step", 
                accessor: "validationStep", 
                id: "validationStep",
                width: 150
            },
            { 
                Header: "Error Code", 
                accessor: "errorCode", 
                id: "errorCode",
                width: 120
            },
            { 
                Header: "Error Message", 
                accessor: "errorMessage", 
                id: "errorMessage",
                Cell: ({ value }) => (
                    <div style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        maxWidth: '300px'
                    }} title={value}>
                        {value}
                    </div>
                )
            },
            {
                Header: "Actions",
                id: "actions",
                className: "d-flex justify-content-center align-items-center",
                Cell: ({ original }) => (
                    <ButtonGroup>
                        <Link to={`/hr/ingestion-failures/${original.id}`} className="btn btn-sm btn-info">
                            <FontAwesomeIcon icon="eye" title="View Details" />
                        </Link>
                    </ButtonGroup>
                ),
                filterable: false,
                width: 100
            }
        ];

        return (
            <div className="dashboard">
                <h3 className="mb-4 d-flex align-items-center justify-content-between">
                    ATS Ingestion Failures
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }} className="text-muted ml-3">
                        Total: {ingestionFailures.length}
                    </span>
                </h3>
                
                <Form className="mb-3">
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Date Range</Form.Label>
                                <RangePicker
                                    value={dateRange}
                                    onChange={this.handleDateRangeChange}
                                    format="YYYY-MM-DD"
                                    style={{ width: '100%' }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="d-flex align-items-end">
                            <Button 
                                variant="primary" 
                                onClick={this.handleSearch}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon="search" className="mr-1" />
                                Search
                            </Button>
                        </Col>
                    </Row>
                </Form>

                <ReactTable
                    data={ingestionFailures}
                    headerClassName="table-header"
                    columns={columns}
                    defaultPageSize={20}
                    pageSizeOptions={[10, 20, 50, 100]}
                    resizable
                    className="-striped -highlight"
                    loading={loading}
                    filterable
                    defaultFilterMethod={filterCaseInsensitive}
                    defaultSorted={[{ id: "occurredUtc", desc: true }]}
                    getNoDataProps={() => ({ style: { zIndex: 0 } })}
                    getTrProps={(state, rowInfo) => {
                        if (rowInfo && rowInfo.original) {
                            const { errorCode } = rowInfo.original;
                            let rowStyle = {};
                            
                            // Highlight critical errors in red
                            if (errorCode && (errorCode.includes('CRITICAL') || errorCode.includes('ERROR'))) {
                                rowStyle.backgroundColor = '#ffcdd2';
                            }
                            
                            return { style: rowStyle };
                        }
                        return {};
                    }}
                    style={{ height: "calc(100vh - 280px)" }}
                />
            </div>
        );
    }
}

IngestionFailuresList.propTypes = {
    fetchIngestionFailures: PropTypes.func.isRequired,
    clearIngestionFailures: PropTypes.func.isRequired,
    ingestionFailures: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
    ingestionFailures: state.ingestionFailures.ingestionFailures,
    loading: state.ingestionFailures.loading
});

const mapDispatchToProps = dispatch => ({
    fetchIngestionFailures: bindActionCreators(fetchIngestionFailures, dispatch),
    clearIngestionFailures: bindActionCreators(clearIngestionFailures, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(IngestionFailuresList);

